import * as React from 'react';
import {useSwipeable} from 'react-swipeable';
import {debounce} from 'throttle-debounce';
import outerWidth from './helpers/outerWidth';

import styles from './styles.module.css';

interface IProps {
  /** number of step */
  step?: number;
  /** show the dot */
  showDot?: boolean;
  /** next element after last */
  nextElement?: JSX.Element;
  children: React.ReactNodeArray;
}

interface ILeaders {
  [key: number]: number;
}

const t = string => string;

/*
 * Carousel with number of step defined
 * all children's outside of container are removed
 * find fist element for each step.
 */
const Carousel: React.FC<IProps> = ({
  nextElement = null,
  showDot = false,
  step = 4,
  children,
}) => {
  const [showCarousel, setShowCarousel] = React.useState<boolean>(true);
  // use children separately in childs for render, used for keep all childrens
  const [childs, setChilds] = React.useState(children);
  // keep current step
  const [current, setCurrent] = React.useState<number>(0);
  // keep last element
  const [outer, setOuter] = React.useState<number>(1000);
  // first element for each step
  const [leaders, setLeaders] = React.useState<ILeaders>({});
  // keep ref
  const _width = React.useRef<number>(0);
  // principal container overflow X hidden
  const _container = React.useRef<HTMLDivElement>(null);
  // container inner wrapper of children element
  const _innerContainer = React.useRef<HTMLDivElement>(null);
  /**
   * init carousel
   * @param force
   */
  const init = (force = false) => {
    if (_container.current !== null && _innerContainer.current !== null) {
      const currentWidth = outerWidth(_container.current);
      const currentD = _container.current.getBoundingClientRect();
      // reduce side effect render
      if (_width.current === currentWidth && force === false) {
        return;
      }
      // reset current
      setCurrent(0);
      // reset all childs
      setChilds(children);
      // save current width for avoid uncontrolled render
      _width.current = currentWidth;
      // reset _outer
      setOuter(1000);
      // children
      const _child = _innerContainer.current.children;

      // first element in not in start after set current to 0
      const first = _child[0];
      const firstD = first.getBoundingClientRect();
      if (firstD.x < 0) {
        setTimeout(() => {
          init(true);
        }, 300);
        return;
      }

      // catch the first element outside max
      let _outer: number = 1000;
      // keep next step to find
      let findStep = 1;
      // kepp next max from each step
      let _nextMax = _width.current;
      // keep x coordinate for move
      const _leader: ILeaders = {};
      // find element out of max
      Object.keys(_child).forEach((i: any) => {
        // get position, width...
        const {right, left, width} = _child[i].getBoundingClientRect();
        // get margin
        const {marginRight, marginLeft} = window.getComputedStyle(_child[i]);
        // find last element
        if (
          (left - currentD.left >= _nextMax ||
            right - currentD.left >= _nextMax) &&
          findStep === step &&
          _outer === 1000
        ) {
          // keep first element out
          _outer = i;
          findStep++;
        }
        // find next leader
        if (
          right - currentD.left > _nextMax &&
          width < _width.current &&
          findStep < step
        ) {
          // position x - margin
          const pos =
            left -
            currentD.left -
            (parseInt(marginRight, 0) + parseInt(marginLeft, 0));
          _leader[findStep] = pos;
          // next step limit width
          _nextMax = pos + _width.current;
          findStep++;
        }

        // find next leader only if element is more width of container
        if (
          right - currentD.left > _nextMax &&
          width > _width.current &&
          findStep < step &&
          i > 0
        ) {
          // position x - margin
          const pos =
            left -
            currentD.left -
            (parseInt(marginRight, 0) + parseInt(marginLeft, 0));
          _leader[findStep] = pos;
          // next step limit width
          _nextMax = pos + _width.current;
          findStep++;
        }
      });

      // update leader state
      setLeaders(_leader);
      // remove last element for set next element
      if (_child[+_outer] !== undefined && nextElement) {
        // last verif
        const {right} = _child[+_outer].getBoundingClientRect();
        // if last is out max width
        if (right >= _nextMax) {
          _outer--;
        }
      }
      setShowCarousel(true);
      // if no children overflow, don't show carousel element
      if (+_outer === 1000 && findStep === 1) {
        setShowCarousel(false);
      }
      // update last
      setOuter(+_outer);
    }
  };
  // debounce resize event callback
  const updateSize = debounce(300, true, init);
  // init and call when childs change
  React.useLayoutEffect(() => init(true), [childs]);
  // listen resize
  React.useEffect(() => {
    window.addEventListener('resize', () => updateSize(false));
    return () => window.removeEventListener('resize', () => updateSize(false));
  }, []);
  // if children is updated
  React.useEffect(() => {
    setChilds(children);
  }, [children]);
  /**
   * got to previous step
   */
  const _goToPrevious = () => {
    setCurrent(current > 0 ? current - 1 : 0);
  };
  /**
   * got to nex slide
   */
  const _goToNext = () => {
    setCurrent(current < step - 1 ? current + 1 : step - 1);
  };
  /**
   * got to n slide
   * @param num
   */
  const _goTo = (num: number) => {
    setCurrent(num > -1 && num < step ? num : current);
  };

  const handlers = useSwipeable({
    delta: 25,
    onSwipedLeft: _goToNext,
    onSwipedRight: _goToPrevious,
    trackMouse: true,
    preventDefaultTouchmoveEvent: false,
  });

  // style for move
  const _styles = {
    transform: `translateX(${current === 0 ? 0 : -leaders[current]}px)`,
  };
  // number of step for dot
  const _stepFound = Object.keys(leaders).length + 1;
  return (
    <div className={styles.wrapper}>
      {showCarousel && (
        <div className={styles.arrow_container}>
          <button disabled={current === 0} onClick={_goToPrevious}>
            <span>{t('previous')}</span>
          </button>
          <button disabled={current === _stepFound - 1} onClick={_goToNext}>
            <span>{t('next')}</span>
          </button>
        </div>
      )}
      <div ref={_container} className={styles.container}>
        <div {...handlers} style={{touchAction: 'pan-y'}}>
          <div
            ref={_innerContainer}
            className={styles.inner_container}
            style={_styles}
          >
            <>
              {childs.slice(0, outer)}
              {_stepFound >= step && nextElement}
            </>
          </div>
        </div>
      </div>
      {showDot && showCarousel ? (
        <div className={styles.dot_container}>
          {Array.from(Array(_stepFound)).map((s, i) => (
            <button
              key={+i}
              disabled={current === +i}
              onClick={() => _goTo(+i)}
            >
              <span>{`${t('Step')} ${+i + 1}${s ? '' : ''}`}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default Carousel;
